/**
 * App.tsx — Root entry point with React Router.
 * Composes: BrowserRouter → AuthProvider → CurrencyProvider → AppShell → Routes
 */
import { useState, useMemo } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom'
import { CurrencyProvider } from './context/CurrencyContext'
import { AuthProvider, useAuth } from './context/AuthContext'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/sonner'
import { AppShell } from './layouts/AppShell'
import { getT } from './i18n'
import type { Lang } from './i18n'

// Feature pages
import LoginPage from './features/auth/LoginPage'
import FinDashboard from '@/features/dashboard/FinDashboard'
import CashFlow from '@/features/cash-flow/CashFlow'
import PivotWorkbench from '@/features/pivot/PivotWorkbench'
import BankAccountsPage from '@/features/accounts/BankAccounts'
import ProfitLoss from '@/features/pl/ProfitLoss'
import PayrollPage from '@/features/payroll/Payroll'
import Projects from '@/features/projects/Projects'
import CapitalPage from '@/features/capital/Capital'
import Entities from '@/features/entities/Entities'
import EntityDetail from '@/features/entities/EntityDetail'
import SettingsPage from '@/features/settings/Settings'
import Budget from '@/features/budget/Budget'
import RecordsPage from '@/features/records/RecordsPage'
import AnalyticsWorkbench from './features/analytics/AnalyticsWorkbench'

export default function App() {
  // ── I18n ──
  const [lang, setLang] = useState<Lang>(() => {
    try { const s = localStorage.getItem("nf_lang"); if (s === "ru" || s === "en") return s } catch {} return "en"
  })
  const t = useMemo(() => getT(lang), [lang])
  const switchLang = (l: Lang) => { setLang(l); try { localStorage.setItem("nf_lang", l) } catch {} }

  // ── Theme ──
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    try { const s = localStorage.getItem("nf_theme"); if (s === "dark") return "dark" } catch {} return "light"
  })
  const toggleTheme = () => setTheme(prev => {
    const next = prev === "dark" ? "light" : "dark"
    document.documentElement.classList.toggle("dark", next === "dark")
    try { localStorage.setItem("nf_theme", next) } catch {}
    return next
  })

  // ── Admin/FAQ modals ──
  const [showAdmin, setShowAdmin] = useState(false)
  const [showFaq, setShowFaq] = useState(false)

  // ENTITIES/YEARS for AuthContext permissions
  const [entities, setEntities] = useState<string[]>(['Wise Wolves Finance'])
  const [years, setYears] = useState<string[]>([])

  return (
    <BrowserRouter>
      <AuthProvider entities={entities} years={years}>
        <CurrencyProvider>
          <TooltipProvider>
            <AppInner
              t={t}
              lang={lang}
              switchLang={switchLang}
              theme={theme}
              toggleTheme={toggleTheme}
              showAdmin={showAdmin}
              setShowAdmin={setShowAdmin}
              showFaq={showFaq}
              setShowFaq={setShowFaq}
              onEntitiesChange={setEntities}
              onYearsChange={setYears}
            />
          </TooltipProvider>
        </CurrencyProvider>
      </AuthProvider>
      <Toaster position="bottom-right" />
    </BrowserRouter>
  )
}

/** Inner component — uses useAuth (must be inside AuthProvider + BrowserRouter) */
function AppInner({
  t, lang, switchLang, theme, toggleTheme,
  showAdmin, setShowAdmin, showFaq, setShowFaq,
  onEntitiesChange, onYearsChange,
}: {
  t: (k: string) => string
  lang: Lang
  switchLang: (l: Lang) => void
  theme: 'light' | 'dark'
  toggleTheme: () => void
  showAdmin: boolean
  setShowAdmin: (v: boolean) => void
  showFaq: boolean
  setShowFaq: (v: boolean) => void
  onEntitiesChange: (e: string[]) => void
  onYearsChange: (y: string[]) => void
}) {
  const { user, login } = useAuth()

  if (!user) {
    return <LoginPage onLogin={login} />
  }

  return (
    <AppShell
      t={t}
      theme={theme}
      toggleTheme={toggleTheme}
      onShowAdmin={() => setShowAdmin(true)}
      onShowFaq={() => setShowFaq(true)}
    >
      <Routes>
        <Route path="/" element={<FinDashboardRoute />} />
        <Route path="/cash-flow" element={<PivotWorkbench />} />
        <Route path="/transactions" element={<CashFlow />} />
        <Route path="/accounts" element={<BankAccountsPage />} />
        <Route path="/pl" element={<ProfitLoss />} />
        <Route path="/payroll" element={<PayrollPage />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/capital" element={<CapitalPage />} />
        <Route path="/budget" element={<Budget />} />
        <Route path="/entities" element={<EntitiesRoute />} />
        <Route path="/entities/:id" element={<EntityDetailRoute />} />
        <Route path="/records" element={<RecordsPage />} />
        <Route path="/records/:object" element={<RecordsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/analytics" element={
          <AnalyticsWorkbench
            t={t}
            lang={lang}
            switchLang={switchLang}
            theme={theme}
            showAdmin={showAdmin}
            onCloseAdmin={() => setShowAdmin(false)}
            showFaq={showFaq}
            onCloseFaq={() => setShowFaq(false)}
            onEntitiesChange={onEntitiesChange}
            onYearsChange={onYearsChange}
          />
        } />
        {/* Catch-all → redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  )
}

// ── Route wrappers (bridge legacy onNavigate props to useNavigate) ──

/** Map old finPage identifiers to router paths */
const PAGE_TO_PATH: Record<string, string> = {
  'fin-dashboard': '/',
  'fin-transactions': '/transactions',
  'fin-pl': '/pl',
  'fin-projects': '/projects',
  'fin-capital': '/capital',
  'fin-payroll': '/payroll',
  'fin-entities': '/entities',
  'fin-accounts': '/accounts',
  'fin-pivot': '/cash-flow',
  'fin-settings': '/settings',
  'entity-detail': '/entities',
}

function useFinNavigate() {
  const navigate = useNavigate()
  return (target: string, params?: Record<string, string>) => {
    const basePath = PAGE_TO_PATH[target] || `/${target.replace('fin-', '')}`
    if (target === 'entity-detail' && params?.id) {
      navigate(`/entities/${params.id}`)
    } else {
      navigate(basePath)
    }
  }
}

function FinDashboardRoute() {
  const onNavigate = useFinNavigate()
  return <FinDashboard onNavigate={onNavigate} />
}

function EntitiesRoute() {
  const onNavigate = useFinNavigate()
  return <Entities onNavigate={onNavigate} />
}

function EntityDetailRoute() {
  const params = useParams()
  const onNavigate = useFinNavigate()
  return <EntityDetail entityId={params.id || ''} onNavigate={onNavigate} />
}
